import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FadeIn } from '../ui/FadeIn';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlay, FiPause, FiSkipForward, FiLink, FiCpu, FiEdit, FiDownload } = FiIcons;

const demoSteps = [
  {
    step: 1,
    title: 'Paste YouTube URL',
    description: 'Simply paste any YouTube video link',
    visual: 'url-input',
    icon: FiLink,
    color: 'text-brand-electric'
  },
  {
    step: 2,
    title: 'AI Analyzes Content',
    description: 'Our AI extracts key concepts and structure',
    visual: 'processing',
    icon: FiCpu,
    color: 'text-brand-neon'
  },
  {
    step: 3,
    title: 'Review & Customize',
    description: 'Edit slides, adjust narration, apply branding',
    visual: 'editing',
    icon: FiEdit,
    color: 'text-brand-mint'
  },
  {
    step: 4,
    title: 'Export & Publish',
    description: 'Download in multiple formats or publish directly',
    visual: 'export',
    icon: FiDownload,
    color: 'text-brand-gold'
  }
];

const InteractiveDemo = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  React.useEffect(() => {
    if (isPlaying) {
      const interval = setInterval(() => {
        setCurrentStep((prev) => (prev + 1) % demoSteps.length);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [isPlaying]);

  return (
    <section className="py-28 relative overflow-hidden bg-gradient-to-b from-brand-dark to-brand-card/20 border-y border-white/5">
      {/* Decorative separators */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-flame/50 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand-flame/50 to-transparent"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <FadeIn className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-6xl font-black mb-6 text-white">See it in action</h2>
          <p className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto">
            Watch how VidRevive transforms content in 4 simple steps
          </p>
        </FadeIn>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-center">
          
          {/* Visual Demo Area */}
          <FadeIn delay={0.2}>
            <div className="glass-card p-6 md:p-8 border border-white/10 min-h-[300px] md:min-h-[400px] flex items-center justify-center relative overflow-hidden">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ duration: 0.5 }}
                  className="w-full"
                >
                  <div className="text-center">
                    {/* Icon Visual */}
                    <div className="w-20 h-20 md:w-28 md:h-28 rounded-2xl bg-gradient-to-br from-brand-neon/20 to-brand-electric/20 mx-auto mb-6 flex items-center justify-center border-2 border-brand-electric/40 shadow-[0_0_40px_rgba(0,229,255,0.4)]">
                      <SafeIcon 
                        icon={demoSteps[currentStep].icon} 
                        className={`text-5xl md:text-6xl ${demoSteps[currentStep].color} icon-glow`} 
                      />
                    </div>

                    {/* Step Number Badge */}
                    <div className="inline-block bg-brand-electric/20 text-brand-electric text-xs font-black px-4 py-1.5 rounded-full mb-4 border border-brand-electric/40">
                      STEP {demoSteps[currentStep].step}/4
                    </div>

                    <h3 className="text-xl md:text-2xl font-bold text-white mb-4">{demoSteps[currentStep].title}</h3>
                    <p className="text-white/70 text-sm md:text-base max-w-md mx-auto">{demoSteps[currentStep].description}</p>
                  </div>
                </motion.div>
              </AnimatePresence>

              {/* Progress Bar */}
              <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
                <motion.div
                  className="h-full bg-gradient-to-r from-brand-neon to-brand-electric"
                  initial={{ width: '0%' }}
                  animate={{ width: `${((currentStep + 1) / demoSteps.length) * 100}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center gap-4 mt-6">
              <button
                onClick={() => setIsPlaying(!isPlaying)}
                className="w-12 h-12 rounded-full bg-brand-electric/20 border border-brand-electric/40 flex items-center justify-center hover:bg-brand-electric/30 transition-all shadow-[0_0_15px_rgba(0,229,255,0.3)]"
              >
                <SafeIcon icon={isPlaying ? FiPause : FiPlay} className="text-brand-electric text-xl" />
              </button>
              <button
                onClick={() => setCurrentStep((prev) => (prev + 1) % demoSteps.length)}
                className="w-12 h-12 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-all"
              >
                <SafeIcon icon={FiSkipForward} className="text-white text-xl" />
              </button>
            </div>
          </FadeIn>

          {/* Step Indicators */}
          <FadeIn delay={0.3}>
            <div className="space-y-3 md:space-y-4">
              {demoSteps.map((step, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentStep(idx)}
                  className={`w-full text-left p-4 md:p-6 rounded-xl border transition-all ${
                    currentStep === idx
                      ? 'bg-brand-electric/10 border-brand-electric/40 shadow-[0_0_20px_rgba(0,229,255,0.2)]'
                      : 'bg-white/5 border-white/10 hover:border-white/20'
                  }`}
                >
                  <div className="flex items-start gap-3 md:gap-4">
                    <div className={`w-12 h-12 md:w-14 md:h-14 rounded-xl flex items-center justify-center shrink-0 border-2 ${
                      currentStep === idx
                        ? 'bg-brand-electric/20 border-brand-electric/40 text-brand-electric'
                        : 'bg-white/5 border-white/10 text-white/60'
                    }`}>
                      <SafeIcon icon={step.icon} className="text-xl md:text-2xl" />
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-bold mb-1 text-sm md:text-base ${
                        currentStep === idx ? 'text-white' : 'text-white/70'
                      }`}>
                        {step.title}
                      </h4>
                      <p className="text-xs md:text-sm text-white/50">{step.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </FadeIn>

        </div>

      </div>
    </section>
  );
};

export default InteractiveDemo;